import React from 'react';
import { IconSlot } from '~/design-system/components/v1_5/Header';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './ZoomCluster.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * The zoom ladder. **CHOSEN IN CODE — NOT MEASURED.** Nothing in Paper enumerates the steps: the
 * label reads `Fit` on every board but one, and the one exception reads `150%` with no ladder,
 * no bounds and no step size drawn anywhere on the page.
 *
 * Two decisions are baked into these numbers, and both are reversible in one line:
 *
 * 1. **1 is Fit, and the percentage is a percentage OF Fit** — not of the artifact's natural size.
 *    This is the only reading that reconciles the one drawn label with the geometry underneath it:
 *    the board's `150%` artifact is 1120px wide where Fit is 760, and 1120 / 760 = 147%. Read as a
 *    percentage of natural size the same label would have meant 1920px, which is not what is drawn.
 * 2. **The ladder is coarse below Fit and generous above it.** Below Fit an artifact is smaller
 *    than it needs to be and there is nothing down there worth many stops; above it you are
 *    inspecting something, and 4x is the point where a 16:9 slide's body text is comfortable.
 */
export const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4] as const;

/** Fit. The default, the label's resting word, and what the readout resets to. */
export const ZOOM_FIT = 1;

/** The next step up, or the top of the ladder. */
export function zoomIn(zoom: number): number {
  return ZOOM_STEPS.find((step) => step > zoom + 1e-6) ?? ZOOM_STEPS[ZOOM_STEPS.length - 1];
}

/** The next step down, or the bottom of the ladder. */
export function zoomOut(zoom: number): number {
  const below = ZOOM_STEPS.filter((step) => step < zoom - 1e-6);
  return below.length > 0 ? below[below.length - 1] : ZOOM_STEPS[0];
}

/**
 * What the readout says. The word at Fit, a rounded percentage everywhere else.
 *
 * The word rather than `100%` because Fit is not a zoom level, it is a promise — that the whole
 * artifact is on screen — and it stops being true the moment the pane is resized while `100%` would
 * still be sitting there claiming otherwise.
 */
export function zoomLabel(zoom: number): string {
  return Math.abs(zoom - ZOOM_FIT) < 1e-6 ? 'Fit' : `${Math.round(zoom * 100)}%`;
}

export interface ZoomClusterProps {
  /** 1 is Fit. See `ZOOM_STEPS`. */
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  /**
   * Lifts the cluster clear of a bottom-centre control. The boards use 70px whenever the edit
   * mode's 42px dock is out — out of scope here, but the offset is the cluster's own business and
   * belongs on its own prop rather than in a caller's stylesheet.
   */
  bottom?: number;
  theme?: HeaderTheme;
  className?: string;
}

/**
 * The 112 x 36 cluster in the bottom-right corner: minus, the readout, plus.
 *
 * It used to end in a rule and a pop-out arrow that opened the artifact on its own. Both went
 * (Beka, 2026-09-05): the pane IS the place the artifact is looked at, and a control that leaves it
 * read as belonging to a browser rather than to the zoom it sat beside. The cluster is now only the
 * three controls that act on the same thing, which is also why it no longer needs a rule inside it.
 *
 * The readout is a real button and not a label. It is the only control in the cluster with no
 * drawn box, which reads as decoration — but it carries a 44px minimum width that nothing else in
 * the design has, and something has to be the way back to Fit once you have stepped away from it.
 * A control that only shows you where you are and cannot take you back is a worse answer than one
 * that looks slightly quieter than its neighbours.
 */
export function ZoomCluster({
  zoom = ZOOM_FIT,
  onZoomChange,
  bottom = 16,
  theme: themeProp,
  className,
}: ZoomClusterProps) {
  const theme = useV15Theme(themeProp);
  const label = zoomLabel(zoom);

  return (
    <div
      className={cn(themes[theme], styles.cluster, className)}
      style={{ '--hv15-zoom-bottom': `${bottom}px` } as React.CSSProperties}
    >
      <IconSlot
        icon="zoom-out"
        glyphSize={15}
        label="Zoom out"
        theme={theme}
        // Never disabled at the bottom of the ladder, and the readout's reason is only half of it.
        // A disabled button cannot hold focus, so a keyboard user who steps down to 50% has the
        // control blurred out from under them and the next Tab restarts at the top of the document.
        // `zoomOut` already clamps, so the press at the end is a no-op rather than a trap.
        onClick={() => onZoomChange?.(zoomOut(zoom))}
        className={styles.button}
      />
      <button
        type="button"
        className={styles.readout}
        onClick={() => onZoomChange?.(ZOOM_FIT)}
        // The visible string is `Fit` or `140%`, neither of which says what pressing it does. It is
        // never disabled at Fit: a control that greys out when it has nothing to do makes the
        // cluster flicker between three live controls and two every time you step back to Fit —
        // and it would drop the keyboard's place when it went. Nothing in this cluster disables.
        aria-label={`Zoom: ${label}. Reset to fit`}
      >
        {label}
      </button>
      <IconSlot
        icon="zoom-in"
        glyphSize={15}
        label="Zoom in"
        theme={theme}
        // Live at 400% for the same reason its twin is live at 50%. See above.
        onClick={() => onZoomChange?.(zoomIn(zoom))}
        className={styles.button}
      />
    </div>
  );
}
